import { useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import CommandPalette from './CommandPalette';
import { useUIStore, useDocumentStore, useAuthStore } from '../lib/store';
import { useCollaboration } from '../lib/useCollaboration';
import { documentsApi } from '../lib/api';
import { cn } from '../lib/utils';

export default function Layout() {
  const { sidebarOpen } = useUIStore();
  const { id: documentId } = useParams<{ id: string }>();
  const { setDocuments, setLoading, documents } = useDocumentStore();
  const { isAuthenticated } = useAuthStore();
  
  // Fetch documents from API on mount
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!isAuthenticated) return;
      
      // Only fetch if we don't have any documents yet
      if (documents.length > 0) return;
      
      setLoading(true);
      try {
        const response = await documentsApi.list();
        if (response.documents) {
          setDocuments(response.documents);
        }
      } catch (error) {
        console.error('Failed to fetch documents:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocuments();
  }, [isAuthenticated, setDocuments, setLoading, documents.length]);
  
  // Get collaboration status if on document page
  const { status, users, lastSaved } = useCollaboration({
    documentId: documentId || '',
    enabled: !!documentId,
  });

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div
        className={cn(
          'flex flex-1 flex-col transition-all duration-300',
          sidebarOpen && 'main-content-with-sidebar'
        )}
      >
        <TopBar
          documentId={documentId}
          status={documentId ? status : undefined}
          users={documentId ? users : undefined}
          lastSaved={documentId ? lastSaved : undefined}
        />
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Command Palette */}
      <CommandPalette />
    </div>
  );
}

